import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardActionArea,
  CardContent,
  CardMedia,
  Pagination,
  TextField, // Added TextField for search input
  InputAdornment, // Added InputAdornment for search icon
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search"; // Added SearchIcon
import articles from "./article";

const ArticlesList = () => {
  const navigate = useNavigate();

  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState(""); // State for search term
  const articlesPerPage = 12;

  // Filtered articles based on search term
  const filteredArticles = articles.filter(
    (article) =>
      article.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      article.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      article.keywords.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const sortedArticles = [...filteredArticles].sort(
    (a, b) => new Date(b.date) - new Date(a.date)
  );

  const totalPages = Math.ceil(sortedArticles.length / articlesPerPage);

  const indexOfLastArticle = currentPage * articlesPerPage;
  const indexOfFirstArticle = indexOfLastArticle - articlesPerPage;
  const currentArticles = sortedArticles.slice(
    indexOfFirstArticle,
    indexOfLastArticle
  );

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setCurrentPage(1); // Reset to first page on search
  };

  return (
    <Box sx={styles.wrapper}>
      <Typography variant="h4" sx={styles.title}>
        📰 Latest Articles
      </Typography>
      <Typography sx={styles.description}>
        SwssDraw isn't just about luck—it's about knowledge too!<br></br>
        Check in daily for smart reads, crypto trends, and NFT game talk that
        keeps you ahead of the curve.
      </Typography>
      <Box sx={styles.searchContainer}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search articles..."
          value={searchTerm}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "#BF1422" }} />
              </InputAdornment>
            ),
          }}
          sx={styles.searchInput}
        />
      </Box>
      <Grid
        container
        spacing={4}
        justifyContent={currentArticles.length === 1 ? "center" : "flex-start"}
      >
        {currentArticles.map((article) => (
          <Grid item xs={12} sm={6} md={4} key={article.id}>
            <Card sx={styles.card}>
              <CardActionArea
                onClick={() => navigate(`/articles/${article.id}`)}
              >
                {article.image && (
                  <CardMedia
                    component="img"
                    image={article.image}
                    alt={article.title}
                    sx={styles.image}
                  />
                )}
                <CardContent>
                  <Typography variant="h6" sx={styles.cardTitle}>
                    {article.title}
                  </Typography>
                  <Typography variant="body2" sx={styles.cardDescription}>
                    {article.description?.slice(0, 120)}...
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>

      {totalPages > 1 && (
        <Box sx={styles.paginationContainer}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
            shape="rounded"
            size="large"
            sx={styles.pagination}
          />
        </Box>
      )}
    </Box>
  );
};

const styles = {
  wrapper: {
    px: { xs: 2, sm: 4 },
    py: 6,
    maxWidth: "1200px",
    mx: "auto",
  },
  title: {
    fontWeight: 700,
    mb: 5,
    textAlign: "center",
    fontFamily: '"Proxima Nova", sans-serif',
    color: "rgba(191, 20, 34, 1)",
    fontSize: " clamp(1.5rem, 2.5vw, 2rem)",
  },
  searchContainer: {
    mb: 4,
    display: "flex",
    justifyContent: { xs: "center", sm: "flex-end" },
  },
  searchInput: {
    width: { xs: "100%", sm: "70%", md: "30%" },

    "& .MuiOutlinedInput-root": {
      borderRadius: "10px",
      "& fieldset": {
        borderColor: "#BF1422",
      },
      "&:hover fieldset": {
        borderColor: "#BF1422",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#BF1422",
        borderWidth: "2px",
      },
    },
    "& .MuiInputBase-input": {
      padding: "5px 5px",
      "&::placeholder": {
        fontSize: "1.3rem", // ✅ Adjusts the placeholder size
        opacity: 0.8,
      },
    },
  },
  card: {
    borderRadius: "16px",
    boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
    transition: "all 0.3s ease",
    backgroundColor: "#fff",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    "&:hover": {
      transform: "translateY(-6px)",
      boxShadow: "0 16px 36px rgba(0,0,0,0.12)",
    },
  },
  image: {
    width: "100%",
    objectFit: "fill",
    aspectRatio: "16/9",
    borderRadius: "0.5em",
  },
  cardTitle: {
    fontWeight: 600,
    color: "#222",
    minHeight: "3.2em",
    fontFamily: '"Proxima Nova", sans-serif',
  },
  cardDescription: {
    mt: 1,
    color: "#666",
    fontSize: "0.95rem",
    lineHeight: 1.6,
    fontFamily: '"Proxima Nova", sans-serif',
  },
  description: {
    textAlign: "center",
    color: "#666",
    fontSize: "1.25rem",
    lineHeight: 1.6,
    fontFamily: '"Proxima Nova", sans-serif',
    marginBottom: "1.5rem",

    "@media (min-width: 768px)": {
      fontSize: "1.5rem",
    },
  },
  paginationContainer: {
    display: "flex",
    justifyContent: "center",
    mt: 4,
  },
  pagination: {
    "& .MuiPaginationItem-root": {
      color: "#BF1422",
      borderRadius: "8px",
      minWidth: "38px",
      height: "38px",
    },
    "& .Mui-selected": {
      backgroundColor: "#BF1422 !important",
      color: "#fff",
    },
  },
};

export default ArticlesList;
